import { appState } from '@/appState';
import { brandEditorService } from '@/brandEditor/brandEditor.service';
import { useStoreGuid } from '@/lib/hooks';
import storeService from '@/store/store.service';
import { Brand } from '@whiz-cart/node-shared/models/brand/brand';
import { defaultBrand } from '@whiz-cart/ui-shared/brand/defaultBrand';
import { useLastDefinedValue } from '@whiz-cart/ui-shared/hooks/useLastDefinedValue';
import { useEffect, useMemo } from 'react';

export default function useCurrentBrand() {
    const { storeGuid, isGlobal } = useStoreGuid();
    const localStorageKey = isGlobal ? 'branding--global' : `branding--store-${storeGuid}`;

    const [config] = storeService.getConfig.useAction(storeGuid);
    const configBranding = config?.branding ?? '';

    const [brand] = brandEditorService.get(configBranding).useCache({ disabled: !config?.branding });
    const overrideBrand = appState.useState((state) => state.overrideBrand);
    const currentBranding = overrideBrand?._id ?? configBranding;

    useEffect(() => {
        if (!brand) {
            return;
        }

        try {
            localStorage.setItem(localStorageKey, JSON.stringify(brand));
        } catch (e) {
            console.error('Error while writing branding to localStorage:', e);
        }
    }, [brand, localStorageKey]);

    const loadedBranding = useMemo(() => {
        if (brand) {
            return brand;
        }

        try {
            const lastStoredJson = localStorage.getItem(localStorageKey);
            if (lastStoredJson !== null) {
                const parsed = JSON.parse(lastStoredJson);
                return Brand.parse(parsed);
            }
        } catch (e) {
            console.error('Error while parsing stored branding:', e);
        }
    }, [brand, localStorageKey]);

    const lastDefinedBrand = useLastDefinedValue(overrideBrand ?? loadedBranding);

    if (currentBranding === defaultBrand._id || lastDefinedBrand?._id === defaultBrand._id) {
        // If default branding is selected, we return `undefined`
        // Since this would be "overwritten" by useLastDefinedValue, we have to handle this case separately
        return undefined;
    }

    return lastDefinedBrand;
}
